import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import operationService from '../../../../services/operationService';
import { OperationBasicItem } from '../../../../services/types/Operation';
import { AutocompleteChips, CustomDropDown } from '../../../../shared/components';
import BasicRadioButton from '../../../../shared/components/BasicRadioButton';
import DropdownChecks from '../../../../shared/components/DropdownChecks';
import { BasicNotification } from '../../../../shared/components/Notification';
import ToggleButton from '../../../../shared/components/ToggleButton';
import useServiceReducer from '../../../../shared/hooks/useServiceReducer';
import OperationContext from '../context';
import reducer, { SearchOperationAction } from './opReducer';

interface Props {
  currentValues?: OperationBasicItem[];
  setRelatedOperationIds: (values: number[]) => void;
  flowType: string;
}

const FlowRelatedSelection = (props: Props) => {
  const { currentValues = [], setRelatedOperationIds, flowType } = props;
  const { t } = useTranslation();
  const { operation } = useContext(OperationContext);
  const [isRelated, setIsRelated] = useState(false);
  const [radioSelected, setRadioSelected] = useState<number | null>(null);
  const [relateds, setRelateds] = useState<string[]>([]);
  const [opRelateds, setOpRelateds] = useState<OperationBasicItem[]>([]);

  const [operations, loadingOperations, , dispatch] = useServiceReducer(
    operationService.getOperationList,
    reducer,
    {
      query: '',
    }
  );

  useEffect(() => {
    if (currentValues.length > 0) {
      setIsRelated(true);
      setRadioSelected(2);
      setOpRelateds(currentValues);
    }
  }, []);

  const setRelatedSelected = (id: string, checked: boolean) => {
    let newValues;
    if (!checked) {
      newValues = [...relateds].filter((x) => x !== id);
    } else {
      newValues = [...relateds, id];
    }
    setRelateds(newValues);
    setRelatedOperationIds(newValues.map((x) => parseInt(x, 10)));
  };

  const handleOperationsSearch = (query: string) => {
    const action = {
      type: 'QUERY_NUMBER',
      query,
    } as SearchOperationAction;
    dispatch(action);
  };

  const maxRelated = () =>
    (flowType === 'INITIAL_ESRS' || flowType === 'ESRS' || flowType === 'SR_ESPF') &&
    opRelateds.length > 4;

  const handleToggleSwitch = (checked: boolean) => {
    setIsRelated(checked);
    if (checked) {
      if (radioSelected === 1) {
        setRelatedOperationIds([...relateds].map((x) => parseInt(x, 10)));
      } else if (radioSelected === 2) {
        setRelatedOperationIds(opRelateds.map((x) => x.id));
      } else {
        setRelatedOperationIds([]);
      }
    } else {
      setRelatedOperationIds([]);
    }
  };

  const handleRadioChange = (value: number) => {
    setRadioSelected(value);
    if (value === 1) {
      setRelatedOperationIds([...relateds].map((x) => parseInt(x, 10)));
    } else if (value === 2) {
      setRelatedOperationIds(opRelateds.map((x) => x.id));
    } else {
      setRelatedOperationIds([]);
    }
  };

  return (
    <div className="flow-related-section">
      <div className="flow-related-section__row">
        <ToggleButton
          htmlId="sw_related"
          checked={isRelated}
          toggleAction={(v) => handleToggleSwitch(v)}
          // disabled={!operation.isMember}
        />
        <span className="ml-2" style={{ fontWeight: isRelated ? 700 : undefined }}>
          {t('This document is related to other operations')}
        </span>
      </div>
      <div className="flow-related-section__row pb-0">
        <BasicRadioButton
          id="rb_flow_create_op_related"
          name="op_related"
          label="Related Operations"
          radioValue={1}
          active={radioSelected === 1}
          onChange={(value) => handleRadioChange(value)}
          className="w-50"
          disabled={!isRelated || operation.relateds.length === 0}
        />
        <BasicRadioButton
          id="rb_flow_create_ops"
          name="op_related"
          label="Other Operations"
          radioValue={2}
          active={radioSelected === 2}
          onChange={(value) => handleRadioChange(value)}
          className="w-50"
          disabled={!isRelated}
        />
      </div>
      <div className="flow-related-section__row pt-0">
        <div className="radio-btn-margin-left w-50" style={{ alignSelf: 'start' }}>
          <CustomDropDown
            text={
              relateds.length >= 1 ? `${relateds.length} ${t('selected')}` : t('selectTheOptions')
            }
            className="custom-filters-dropdown"
            disabled={!isRelated || radioSelected !== 1 || operation.relateds.length === 0}
          >
            <DropdownChecks
              elements={operation.relateds.map((x) => ({
                id: x.id.toString(),
                name: x.number,
              }))}
              selected={relateds}
              setSelected={(id, checked) => {
                setRelatedSelected(id, checked);
              }}
            />
          </CustomDropDown>
        </div>
        <div className="radio-btn-margin-left w-50">
          <AutocompleteChips<OperationBasicItem>
            labelKey="number"
            htmlId="ops-typeahead"
            list={operations}
            selected={opRelateds}
            placeholder="Ex. PE-L1099"
            onSearch={handleOperationsSearch}
            onChange={(selected) => {
              setOpRelateds(selected);
              setRelatedOperationIds(selected.map((x) => x.id));
            }}
            loading={loadingOperations}
            render={(op: OperationBasicItem) => <div>{op.number}</div>}
            className="mb-1"
            multiple
            disabled={!isRelated || radioSelected !== 2}
          />
        </div>
      </div>
      {maxRelated() && (
        <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
          <BasicNotification
            message={t('warningMaxOperationsRelated')}
            color="warning"
            icon="exclamation-triangle"
          />
        </div>
      )}
    </div>
  );
};

export default FlowRelatedSelection;
